import React, {
	memo,
	useMemo,
	useState,
	useCallback,
	useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
	Modal,
	Button
} from 'semantic-ui-react';

import { log } from '../../../../services/utils';

import DynamicForm from '../../DynamicForm';
import useForm from '../../../../hooks/useForm';

import styles from '../../document-modal.module.css';

const CollectionModal = ( {
	admin,
	closeModal,
	data,
	label,
	onSubmit,
	open,
	sections,
	ui
} ) => {
	const fieldTypes = useMemo(
		() => ui.reduce(
			(
				acc,
				{
					id,
					type
				}
			) => ( {
				...acc,
				[ id ]: type
			}
			),
			{}
		),
		[ ui ]
	);

	const toFormData = useCallback( data => data.reduce( (
		acc,
		{
			questionId,
			questionOptionId,
			value
		}
	) => {
		switch ( fieldTypes[ questionId ] ) {
			case 6: // select
			case 10: // radio
				return {
					...acc,
					[ questionId ]: questionOptionId
				};

			default:
				return {
					...acc,
					[ questionId ]: value
				};
		}
	}, {} ), [ fieldTypes ] );

	const {
		formData,
		setFormData,
		updateField
	} = useForm( toFormData( data ) );

	const toData = useCallback(
		formData => data.length
			? data.map( ( {
				questionId,
				...rest
			} ) => {
				switch ( fieldTypes[ questionId ] ) {
					case 6: // select
					case 10: // radio
						return {
							...rest,
							questionId,
							questionOptionId: formData[ questionId ]
						};

					default: // everything else
						return {
							...rest,
							questionId,
							value: formData[ questionId ]
						};
				}
			} )
			: Object.entries( formData ).map( ( [
				questionId,
				value
			] ) => {
				switch ( fieldTypes[ questionId ] ) {
					case 6: // select
					case 10: // radio
						return {
							id: 0,
							active: false,
							questionId: Number( questionId ),
							questionOptionId: value,
							value: ''
						};

					default: // everything else
						return {
							id: 0,
							active: false,
							questionId: Number( questionId ),
							questionOptionId: 0,
							value
						};
				}
			} ),
		[
			data,
			fieldTypes
		]
	);

	const [ validateModule, setValidateModule ] = useState( false );
	const [ moduleErrors, setModuleErrors ] = useState( false );

	useEffect( () => {
		setFormData( toFormData( data ) );
	}, [
		data,
		setFormData,
		toFormData
	] );

	const handleSave = useCallback( () => {
		if ( moduleErrors ) {
			setValidateModule( true );
		} else {
			setValidateModule( false );
			onSubmit( toData( formData ) );
			closeModal();
		}
	}, [
		closeModal,
		formData,
		moduleErrors,
		onSubmit,
		toData
	] );

	const handleClose = useCallback( () => {
		if ( validateModule ) {
			setValidateModule( false );
		}

		closeModal();
	}, [
		closeModal,
		validateModule
	] );

	const header = useMemo( () => Object.keys( data ).length
		? `Edit ${ label }`
		: `Add New ${ label }`, [
		data,
		label
	] );

	return (
		<Modal
			className={ styles.collectionModal }
			open={ open }
			size="small"
		>
			{ log( '- collectionModal' ) }
			<Modal.Header>
				{ header }
			</Modal.Header>
			<DynamicForm
				admin={ admin }
				formData={ formData }
				sections={ sections }
				setModuleErrors={ setModuleErrors }
				ui={ ui }
				updateField={ updateField }
				validate={ validateModule }
			/>
			<Modal.Actions>
				<Button
					content="Save"
					color="pink"
					onClick={ handleSave }
				/>
				<Button
					content="Cancel"
					onClick={ handleClose }
				/>
			</Modal.Actions>
		</Modal>
	);
};

CollectionModal.propTypes = {
	admin: PropTypes.bool,
	closeModal: PropTypes.func.isRequired,
	data: PropTypes.array,
	instructions: PropTypes.string,
	label: PropTypes.string.isRequired,
	onSubmit: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	sections: PropTypes.array.isRequired,
	ui: PropTypes.array.isRequired
};

export default memo( CollectionModal );
